/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { get, isEmpty, isNumber, set, uniqueId } from 'lodash';
import Draggable from 'vuedraggable';

// Components
import ConditionsEditor from 'shared/ui/components/form-components/targeting/ConditionsEditor.vue';
import DropdownMenu from 'shared/ui/components/DropdownMenu.vue';
import DynamicFields from 'shared/ui/components/form-components/DynamicFields.vue';
import NewFormSection from 'shared/ui/components/registrations/NewFormSection.vue';

// Mixins
import form from 'shared/ui/components/registrations/form';

const FORBIDDEN_CONDITIONAL_KINDS = ['email', 'text'];

const __vue_script__ = {
    name: 'FormFields',

    components: { ConditionsEditor, Draggable, DropdownMenu, DynamicFields, NewFormSection },

    mixins: [form],

    props: {
        settings: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            allFields: null,
            collapsedSections: [],
            conditionsEditing: '',
            editing: '',
            editingAllowed: this.settings.allow_profile_updates,
            fields: [],
            legalDocs: [],
            metadataFields: [],
            ready: false,
            showNewFormSection: false,
            skipReload: false,
            sortedSections: []
        };
    },

    computed: {
        /** @returns {string[]} */
        compatibleFields() {
            return this.allFields.filter(f => {
                return f.kind && this.$services.metadata.TARGETING_SUPPORTED_KINDS
                    .filter(k => !FORBIDDEN_CONDITIONAL_KINDS.includes(k))
                    .includes(f.kind);
            });
        },

        /** @returns {boolean} */
        hasCompatibleFields() {
            return this.compatibleFields.length > 0;
        },

        /** @returns {string|null} */
        addConditionTooltip() {
            if (!this.hasCompatibleFields) {
                return this.$i18n.t('registration.configuration.form.missing_compatible_fields').toString();
            }

            return null;
        }
    },

    watch: {
        value: {
            immediate: true,
            handler() {
                this.init();
            }
        }
    },

    methods: {
        async init() {
            try {
                this.legalDocs = await this.$services.pax.getLegalDocumentsByEid(this.event._id, true);
                this.metadataFields = await this.$services.metadata.getUserRegistrationFields(this.event._id);

                const sections = this.getSectionNames();
                for (const section of sections) {
                    this.fields[section] = this.getFieldsFor(section);
                }

                this.computeSorting();
                await this.computeTargetingFields();

                this.editing = '';
                this.conditionsEditing = '';
                this.ready = true;

            } catch (error) {
                console.error('[FormFields] Could not initialize form with default values', error.message);
            }
        },

        /**
         * get all the fields (of private and hybrid reg)
         */
        async computeTargetingFields() {
            const metadata = await this.$services.metadata.getTargetingMetadata(this.event);
            this.allFields = this.$utils.metadata.convertMetadataHashToOrderedList(metadata);
        },

        /**
         * Gets the fields exclusion list for the given section
         *
         * @param {string} section the name of the section
         *
         * @returns {string[]} a list of fields to exclude from the dynamic dropdown
         */
        getExclusions(section) {
            const exclude = ['self_registered']; // should never be a possible field
            if (section !== 'main') {
                const fields = this.innerValue['main'].fields.map(f => f.id || f.name).filter(f => f);
                exclude.push(...fields);
            }
            return exclude;
        },

        /**
         * Computes the sections sorting and their name
         */
        computeSorting() {
            const unsortedSections = [];
            for (const [key, value] of Object.entries(this.innerValue)) {
                if (key.startsWith('_')) {
                    continue;
                }

                // Shallow storing the key value
                value.__key = key;
                value.name = value.name || key;

                if (!value.hasOwnProperty('order')) {
                    // This will happen during the first round of migration
                    // where the `main` form has not an `order` field yet.
                    this.setOrderFor(key, 0);
                }

                unsortedSections.push(value);
            }

            const sections = this.$utils.collections.sortByOrder(unsortedSections);
            this.sortedSections = sections.map(s => s.__key);

            // Cleanup
            sections.forEach(s => delete s.__key);
        },

        /**
         * Given a scope, this method returns
         * the fields for that form.
         *
         * @param {string} scope the scope of the form
         *
         * @returns {object[]}
         */
        getFieldsFor(scope) {
            return get(this.innerValue, `${scope}.fields`, []).filter(f => f);
        },

        /**
         * Given a scope, this method sets
         * the fields for that form.
         *
         * @param {string} scope the scope of the form
         * @param {object[]} [fields] the fields of the form
         */
        setFieldsFor(scope, fields = []) {
            set(this.innerValue, `${scope}.fields`, fields);
        },

        /**
         * Given a scope, this method returns
         * the fields for that form.
         *
         * @param {string} scope the scope of the form
         *
         * @returns {object[]}
         */
        getNameFor(scope) {
            return get(this.innerValue, `${scope}.name`, this.$i18n.t('registration.configuration.form.basic_info'));
        },

        /**
         * Given a scope, this method returns
         * the description for that form if exists
         *
         * @param {string} scope the scope of the form
         * @returns {string|undefined}
         */
        getDescriptionFor(scope) {
            return get(this.innerValue, `${scope}.description`);
        },

        /**
         * Given a scope, this method sets
         * the name for that form.
         *
         * @param {string} scope the scope of the form
         * @param {string} name the name of the form
         */
        setNameFor(scope, name) {
            set(this.innerValue, `${scope}.name`, name);
        },

        /**
         * Given a scope, this method sets
         * the order for that section.
         *
         * @param {string} scope the scope of the form
         * @param {number} [order] optional order to set
         */
        setOrderFor(scope, order) {
            set(this.innerValue, `${scope}.order`, isNumber(order) ? order : this.sortedSections.length);
        },

        /**
         * Input event handler
         *
         * @param {string} scope the scope of the form
         * @param {object[]} fields
         */
        onInput(scope, fields) {
            this.setFieldsFor(scope, fields);
            this.$emit('input', this.innerValue);
        },

        /**
         * Adds a new section to the form
         *
         * @param {string} name the name of the section
         */
        addNewSection(name) {
            console.debug('[FormFields] Adding new section', name);
            this.showNewFormSection = false;
            let sectionName = this.$utils.strings.parameterize(name);

            // This cover a very special case where `name` do not contains any ASCII char (a title in korean for instance)
            // IN this case `parameterize(name)` returns an empty string
            if (isEmpty(sectionName)) {
                sectionName = uniqueId();
            }
            if (this.sortedSections.includes(sectionName)) {
                sectionName = uniqueId(`${sectionName}-`);
            }

            this.setNameFor(sectionName, name);
            this.setFieldsFor(sectionName);
            this.setOrderFor(sectionName);
            this.computeSorting();

            this.$emit('input', this.innerValue);
        },

        /**
         * Gets all the sections name (excluding the protected ones)
         *
         * @returns {string[]} the sections' list
         */
        getSectionNames() {
            return Object.keys(this.innerValue).filter(s => !s.startsWith('_'));
        },

        /**
         * Starts the section name editing
         *
         * @param {string} section the section to edit
         */
        rename(section) {
            this.editing = section;
        },

        /**
         * Changes the name of the section
         *
         * @param {string} section the name of the section
         * @param {string} name the value of the name of the section
         */
        changeName(section, name) {
            this.setNameFor(section, name);
            this.editing = '';
        },

        /**
         * Removes the section from the list
         *
         * @param {string} section the name of the section to remove
         */
        removeSection(section) {
            if (this.isReadOnly(section)) {
                console.warn('[FormFields] Cannot remove read only section', section);
                return;
            }

            delete this.innerValue[section];

            this.computeSorting();
            this.$emit('input', this.innerValue);
        },

        /**
         * Checks whether the move is allowed or not
         *
         * @param {object} event the sortable move event
         * @param {object} event.draggedContext info regarding the dragging object
         * @param {object} event.relatedContext info regarding the related object
         *
         * @returns {boolean} whether the move is allowed or forbidden
         */
        checkPosition({ draggedContext, relatedContext }) {
            if (this.hasFixedOrder(draggedContext.element) || (relatedContext?.element && this.hasFixedOrder(relatedContext.element))) {
                return false;
            }

            return draggedContext.futureIndex !== 0 && draggedContext.index !== 0;
        },

        /**
         * Sets the correct sorting order for each section
         */
        reconcileSorting() {
            for (const [index, section] of this.sortedSections.entries()) {
                this.setOrderFor(section, index);
            }
            this.$emit('input', this.innerValue);
        },

        /**
         * Toggles the specified section
         *
         * @param {string} section the section name to toggle
         */
        toggleSection(section) {
            if (this.collapsedSections.includes(section)) {
                this.collapsedSections = this.collapsedSections.filter(s => s !== section);
            } else {
                this.collapsedSections.push(section);
            }
        },

        /**
         * Shows the section conditions editor
         *
         * @param {string} section the section name to toggle
         */
        editConditions(section) {
            if (this.isReadOnly(section)) {
                console.warn('[FormFields] Cannot edit read only conditions', section);
                return;
            }

            if (!this.hasCompatibleFields) {
                return;
            }

            this.conditionsEditing = section;
        },

        /**
         * Resets the conditions and closes the editor
         */
        clearConditions(section) {
            if (this.isReadOnly(section)) {
                console.warn('[FormFields] Cannot clear read only conditions', section);
                return;
            }

            delete this.innerValue[section].conditions;
            this.conditionsEditing = '';
        },

        /**
         * Checks if the given section is marked as readonly
         *
         * @param {string} section the section name to check
         *
         * @returns {boolean}
         */
        isReadOnly(section) {
            return Boolean(this.innerValue[section].read_only);
        },

        /**
         * Checks if the given section has a fixed order
         *
         * @param {string} section the section name to check
         *
         * @returns {boolean}
         */
        hasFixedOrder(section) {
            return Boolean(this.innerValue[section].fixed_order);
        },

        /**
         * Checks if the condition editor should be visible for the given section or not
         *
         * @param {string} section the section name to check
         *
         * @returns {boolean}
         */
        shouldShowConditionsForSection(section) {
            return this.conditionsEditing === section || !isEmpty(this.innerValue[section].conditions);
        },

        /**
         * Checks if the condition editor button should be visible for the given section or not
         *
         * @param {string} section the section name to check
         *
         * @returns {boolean}
         */
        shouldShowConditionsButtonForSection(section) {
            if (section === 'main' || this.isReadOnly(section)) {
                return false;
            }

            if (this.collapsedSections.includes(section)) {
                return false;
            }

            return (!this.shouldShowConditionsForSection(section) && this.hasCompatibleFields) || !this.hasCompatibleFields;
        },

        /**
         * Checks whether the section's actions should be displayed
         *
         * @param {string} section  the section name
         *
         * @returns {boolean}
         */
        shouldShowSectionActions(section) {
            return this.shouldShowRenameButton(section) || this.shouldShowRemoveButton(section);
        },

        /**
         * Checks whether the section's rename button should be displayed
         *
         * @param {string} section  the section name
         *
         * @returns {boolean}
         */
        shouldShowRenameButton(section) {
            return !this.isReadOnly(section);
        },

        /**
         * Checks whether the section's remove button should be displayed
         *
         * @param {string} section  the section name
         *
         * @returns {boolean}
         */
        shouldShowRemoveButton(section) {
            return section !== 'main' && !this.isReadOnly(section);
        }
    }
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c(
    "div",
    { staticClass: "form-fields" },
    [
      _c("header", { staticClass: "subpage-heading" }, [
        _c("div", { staticClass: "page-actions" }, [
          _c(
            "a",
            {
              staticClass: "btn btn-primary btn-ctx-icon",
              on: {
                click: function ($event) {
                  _vm.showNewFormSection = true
                },
              },
            },
            [
              _vm._v(
                _vm._s(_vm.$t("registration.configuration.actions.add_section"))
              ),
              _c("i", { staticClass: "ic-plus" }),
            ]
          ),
        ]),
      ]),
      _vm.ready
        ? _c(
            "validation-observer",
            { ref: "form", attrs: { tag: "form", autocomplete: "off" } },
            [
              _c(
                "draggable",
                {
                  attrs: { move: _vm.checkPosition, filter: ".fixed-order" },
                  on: { end: _vm.reconcileSorting },
                  model: {
                    value: _vm.sortedSections,
                    callback: function ($$v) {
                      _vm.sortedSections = $$v
                    },
                    expression: "sortedSections",
                  },
                },
                _vm._l(_vm.sortedSections, function (section) {
                  return _c("validation-provider", {
                    key: section,
                    attrs: { slim: true, name: "fields[" + section + "]" },
                    scopedSlots: _vm._u(
                      [
                        {
                          key: "default",
                          fn: function (ref) {
                            var errors = ref.errors
                            return [
                              _c("dynamic-fields", {
                                class: {
                                  collapsed:
                                    _vm.collapsedSections.includes(section),
                                  "fixed-order": _vm.hasFixedOrder(section),
                                },
                                attrs: {
                                  event: _vm.event,
                                  "dropdown-anchor": "nw",
                                  name: "fields[" + section + "]",
                                  fields: _vm.metadataFields,
                                  "legal-docs": _vm.legalDocs,
                                  "is-session-reg-enabled":
                                    _vm.options.sessionRegistrationEnabled ===
                                    true,
                                  errors: errors,
                                  exclusions: _vm.getExclusions(section),
                                  "include-required": section === "main",
                                  "manual-approval":
                                    _vm.settings.manual_approval,
                                  "capacity-enabled":
                                    _vm.settings.self_registered_user_limit >
                                      0 ||
                                    _vm.settings.self_registered_waiting_list,
                                  "self-registered-waiting-list":
                                    _vm.settings.access_type !== "private" &&
                                    _vm.settings.self_registered_waiting_list,
                                  "show-editing": _vm.editingAllowed,
                                  "hide-add-field-btn":
                                    _vm.innerValue[section].hide_add_field_btn,
                                  "hide-legal-dropdown":
                                    _vm.innerValue[section].hide_legal_dropdown,
                                  "hide-session-registration-btn":
                                    _vm.innerValue[section]
                                      .hide_session_registration_btn,
                                },
                                on: {
                                  input: function (data) {
                                    return _vm.onInput(section, data)
                                  },
                                  saveModalData: function ($event) {
                                    return _vm.validateAndSaveChanges()
                                  },
                                },
                                scopedSlots: _vm._u(
                                  [
                                    {
                                      key: "top-header",
                                      fn: function (ref) {
                                        var selectedFields = ref.selectedFields
                                        return [
                                          _vm.editing !== section
                                            ? _c(
                                                "div",
                                                {
                                                  staticClass:
                                                    "title-and-actions",
                                                },
                                                [
                                                  _c(
                                                    "div",
                                                    { staticClass: "actions" },
                                                    [
                                                      _vm.shouldShowConditionsButtonForSection(
                                                        section
                                                      )
                                                        ? _c(
                                                            "a",
                                                            {
                                                              directives: [
                                                                {
                                                                  name: "tooltip",
                                                                  rawName:
                                                                    "v-tooltip",
                                                                  value:
                                                                    _vm.addConditionTooltip,
                                                                  expression:
                                                                    "addConditionTooltip",
                                                                },
                                                              ],
                                                              staticClass:
                                                                "has-action add-conditions",
                                                              attrs: {
                                                                disabled:
                                                                  !_vm.hasCompatibleFields,
                                                              },
                                                              on: {
                                                                click:
                                                                  function (
                                                                    $event
                                                                  ) {
                                                                    return _vm.editConditions(
                                                                      section
                                                                    )
                                                                  },
                                                              },
                                                            },
                                                            [
                                                              _vm._v(
                                                                _vm._s(
                                                                  _vm.$t(
                                                                    "registration.configuration.actions.add_condition"
                                                                  )
                                                                )
                                                              ),
                                                              !_vm.hasCompatibleFields
                                                                ? _c("i", {
                                                                    staticClass:
                                                                      "ic-info-fill",
                                                                  })
                                                                : _vm._e(),
                                                            ]
                                                          )
                                                        : _vm._e(),
                                                      selectedFields.length
                                                        ? _c(
                                                            "a",
                                                            {
                                                              staticClass:
                                                                "toggler",
                                                              on: {
                                                                click:
                                                                  function (
                                                                    $event
                                                                  ) {
                                                                    return _vm.toggleSection(
                                                                      section
                                                                    )
                                                                  },
                                                              },
                                                            },
                                                            [
                                                              _c("i", {
                                                                staticClass:
                                                                  "ic-chevron-up",
                                                              }),
                                                            ]
                                                          )
                                                        : _vm._e(),
                                                      _vm.shouldShowSectionActions(
                                                        section
                                                      )
                                                        ? _c(
                                                            "dropdown-menu",
                                                            {
                                                              staticClass:
                                                                "embedded",
                                                            },
                                                            [
                                                              _c("ul", [
                                                                _c("li", [
                                                                  _vm.shouldShowRenameButton(
                                                                    section
                                                                  )
                                                                    ? _c(
                                                                        "a",
                                                                        {
                                                                          staticClass:
                                                                            "rename",
                                                                          attrs:
                                                                            {
                                                                              href: "",
                                                                            },
                                                                          on: {
                                                                            click:
                                                                              function (
                                                                                $event
                                                                              ) {
                                                                                $event.preventDefault()
                                                                                return _vm.rename(
                                                                                  section
                                                                                )
                                                                              },
                                                                          },
                                                                        },
                                                                        [
                                                                          _c(
                                                                            "i",
                                                                            {
                                                                              staticClass:
                                                                                "ic-edit",
                                                                            }
                                                                          ),
                                                                          _vm._v(
                                                                            _vm._s(
                                                                              _vm.$t(
                                                                                "registration.configuration.actions.rename"
                                                                              )
                                                                            )
                                                                          ),
                                                                        ]
                                                                      )
                                                                    : _vm._e(),
                                                                ]),
                                                                _vm.shouldShowRemoveButton(
                                                                  section
                                                                )
                                                                  ? _c("li", [
                                                                      _c(
                                                                        "a",
                                                                        {
                                                                          staticClass:
                                                                            "delete danger",
                                                                          attrs:
                                                                            {
                                                                              href: "",
                                                                            },
                                                                          on: {
                                                                            click:
                                                                              function (
                                                                                $event
                                                                              ) {
                                                                                $event.preventDefault()
                                                                                return _vm.removeSection(
                                                                                  section
                                                                                )
                                                                              },
                                                                          },
                                                                        },
                                                                        [
                                                                          _c(
                                                                            "i",
                                                                            {
                                                                              staticClass:
                                                                                "ic-trash-2",
                                                                            }
                                                                          ),
                                                                          _vm._v(
                                                                            _vm._s(
                                                                              _vm.$t(
                                                                                "registration.configuration.actions.remove"
                                                                              )
                                                                            )
                                                                          ),
                                                                        ]
                                                                      ),
                                                                    ])
                                                                  : _vm._e(),
                                                              ]),
                                                            ]
                                                          )
                                                        : _vm._e(),
                                                    ],
                                                    1
                                                  ),
                                                  section !== "main" &&
                                                  !_vm.hasFixedOrder(section)
                                                    ? _c(
                                                        "div",
                                                        {
                                                          staticClass:
                                                            "drag-handler",
                                                        },
                                                        [
                                                          _c("i", {
                                                            staticClass:
                                                              "icon-reorder",
                                                          }),
                                                        ]
                                                      )
                                                    : _vm._e(),
                                                  _c(
                                                    "span",
                                                    { staticClass: "title" },
                                                    [
                                                      _vm._v(
                                                        _vm._s(
                                                          _vm.getNameFor(
                                                            section
                                                          )
                                                        )
                                                      ),
                                                    ]
                                                  ),
                                                  _vm.getDescriptionFor(section)
                                                    ? _c(
                                                        "span",
                                                        {
                                                          staticClass:
                                                            "description",
                                                        },
                                                        [
                                                          _vm._v(
                                                            _vm._s(
                                                              _vm.getDescriptionFor(
                                                                section
                                                              )
                                                            )
                                                          ),
                                                        ]
                                                      )
                                                    : _vm._e(),
                                                  _vm.shouldShowConditionsForSection(
                                                    section
                                                  )
                                                    ? _c("conditions-editor", {
                                                        attrs: {
                                                          "read-only":
                                                            _vm.isReadOnly(
                                                              section
                                                            ),
                                                          fields:
                                                            _vm.compatibleFields,
                                                        },
                                                        on: {
                                                          empty: function (
                                                            $event
                                                          ) {
                                                            return _vm.clearConditions(
                                                              section
                                                            )
                                                          },
                                                        },
                                                        model: {
                                                          value:
                                                            _vm.innerValue[
                                                              section
                                                            ].conditions,
                                                          callback: function (
                                                            $$v
                                                          ) {
                                                            _vm.$set(
                                                              _vm.innerValue[
                                                                section
                                                              ],
                                                              "conditions",
                                                              $$v
                                                            )
                                                          },
                                                          expression:
                                                            "innerValue[section].conditions",
                                                        },
                                                      })
                                                    : _vm._e(),
                                                ],
                                                1
                                              )
                                            : _vm._e(),
                                          _vm.editing === section
                                            ? _c(
                                                "div",
                                                {
                                                  staticClass:
                                                    "title-and-actions editing",
                                                },
                                                [
                                                  _c("new-form-section", {
                                                    attrs: {
                                                      sections:
                                                        _vm.sortedSections,
                                                      "initial-value":
                                                        _vm.getNameFor(section),
                                                      editing: true,
                                                    },
                                                    on: {
                                                      input: function (name) {
                                                        return _vm.changeName(
                                                          section,
                                                          name
                                                        )
                                                      },
                                                      close: function ($event) {
                                                        _vm.editing = ""
                                                      },
                                                    },
                                                  }),
                                                ],
                                                1
                                              )
                                            : _vm._e(),
                                        ]
                                      },
                                    },
                                  ],
                                  null,
                                  true
                                ),
                                model: {
                                  value: _vm.fields[section],
                                  callback: function ($$v) {
                                    _vm.$set(_vm.fields, section, $$v)
                                  },
                                  expression: "fields[section]",
                                },
                              }),
                            ]
                          },
                        },
                      ],
                      null,
                      true
                    ),
                  })
                }),
                1
              ),
            ],
            1
          )
        : _vm._e(),
      _vm.showNewFormSection
        ? _c("new-form-section", {
            attrs: { sections: _vm.sortedSections },
            on: {
              input: _vm.addNewSection,
              close: function ($event) {
                _vm.showNewFormSection = false
              },
            },
          })
        : _vm._e(),
    ],
    1
  )
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/registrations/FormFields.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__